/**
 * BookingFlows Platform Analytics
 * ================================
 * Central place for all GA4 + Google Ads event tracking.
 *
 * B2B funnel  (operator acquisition):
 *   1. supplier_page_view   — operator lands on /for-suppliers, /for-charters, /for-tours, /for-rentals
 *   2. signup_started       — operator clicks "Start Free" and reaches /signup
 *   3. operator_signup      — operator completes signup (CONVERSION — ties to Google Ads B2B campaign)
 *
 * B2C funnel  (marketplace bookings):
 *   1. marketplace_search   — customer searches or browses /search
 *   2. service_view         — customer opens a service detail page
 *   3. checkout_started     — customer clicks "Select date" and enters booking flow
 *   4. marketplace_booking  — customer completes a paid booking (CONVERSION — ties to B2C campaign)
 */

// Helper — only fires if gtag is loaded (graceful fallback)
function _gtag(...args) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag(...args);
  }
}

// Helper — fires a Google Ads conversion with value
function _gadsConversion(conversionLabel, value = 0, currency = 'USD') {
  if (!conversionLabel || conversionLabel.includes('REPLACE_')) return; // label not configured yet
  _gtag('event', 'conversion', {
    send_to: conversionLabel,
    value,
    currency,
  });
}

// ─── Meta (Facebook) Pixel — dormant until REACT_APP_META_PIXEL_ID is set ───

const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '1879485989648740';

/** Idempotent Meta Pixel loader. No-op when the env var is empty. */
export function initMetaPixel() {
  if (!META_PIXEL_ID || typeof window === 'undefined' || window.fbq) return;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', META_PIXEL_ID);
  // NOTE: intentionally NO PageView here — bfPageView() (route-aware, in
  // LiveViewersBadge) is the single PageView source: exactly 1 per route.
}

// Helper — fires ONLY to the BookingFlows pixel (trackSingle, never broadcast)
function _fbq(event, params = {}, eventId) {
  if (META_PIXEL_ID && typeof window !== 'undefined' && typeof window.fbq === 'function') {
    if (eventId) window.fbq('trackSingle', META_PIXEL_ID, event, params, { eventID: eventId });
    else window.fbq('trackSingle', META_PIXEL_ID, event, params);
  }
}

// Custom (non-standard) events — kept out of Meta's standard funnel
function _fbqCustom(event, params = {}) {
  if (META_PIXEL_ID && typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('trackSingleCustom', META_PIXEL_ID, event, params);
  }
}

// ─── B2B Events ─────────────────────────────────────────────────────────────

/** Call when operator lands on any B2B landing page (/for-suppliers, /for-charters, etc.) */
export function trackSupplierPageView(page = '/for-suppliers') {
  _gtag('event', 'supplier_page_view', {
    event_category: 'B2B',
    page_path: page,
  });
  // Soft lead signal for Google Ads (warm audience)
  _gadsConversion(window.BF_CONVERSIONS?.supplier_lead);
}

/** Call when operator clicks "Start Free" and navigates to /signup */
export function trackSignupStarted(source = '') {
  _gtag('event', 'signup_started', {
    event_category: 'B2B',
    source,
  });
}

/** Call when operator successfully creates an account — PRIMARY B2B CONVERSION */
export function trackOperatorSignup({ email, plan = 'free', source = '' } = {}) {
  // GA4 standard sign_up event
  _gtag('event', 'sign_up', {
    method: source || 'email',
  });
  // Custom B2B event with extra context
  _gtag('event', 'operator_signup', {
    event_category: 'B2B',
    plan,
    source,
  });
  // Google Ads conversion — this is what Google Ads optimises toward
  _gadsConversion(window.BF_CONVERSIONS?.operator_signup, 0, 'USD');
}

// ─── B2C Events ─────────────────────────────────────────────────────────────

/** Call when customer performs a marketplace search */
export function trackMarketplaceSearch({ query = '', city = '', category = '', country = '' } = {}) {
  _gtag('event', 'search', {
    search_term: query || city || category,
    event_category: 'B2C',
    city,
    category,
    country,
  });
  _fbq('Search', { search_string: query || city || category, content_category: category });
}

/** Call when customer opens a service / experience detail */
export function trackServiceView({ serviceId, serviceName, price = 0, currency = 'USD', orgName = '' } = {}) {
  _gtag('event', 'view_item', {
    event_category: 'B2C',
    items: [{
      item_id: serviceId,
      item_name: serviceName,
      price,
      item_brand: orgName,
    }],
    currency,
    value: price,
  });
  _fbq('ViewContent', { content_ids: [serviceId], content_name: serviceName, content_type: 'product', value: price, currency });
}

/** Call when customer enters the booking flow (clicks "Select date") */
export function trackCheckoutStarted({ serviceId, serviceName, price = 0, currency = 'USD', orgName = '' } = {}) {
  _gtag('event', 'begin_checkout', {
    event_category: 'B2C',
    items: [{
      item_id: serviceId,
      item_name: serviceName,
      price,
      item_brand: orgName,
    }],
    currency,
    value: price,
  });
  // Demoted from InitiateCheckout: the canonical InitiateCheckout fires in the
  // booking widget when the customer commits to payment (pre-Stripe). This is
  // just the "Book Now" click — a custom intent signal, not a funnel stage.
  _fbqCustom('MarketplaceBookNow', { content_ids: [serviceId], content_name: serviceName, value: price, currency });
}

/** Call when customer completes a paid booking — PRIMARY B2C CONVERSION */
export function trackMarketplaceBooking({
  confirmationCode,
  serviceId,
  serviceName,
  totalPrice = 0,
  currency = 'USD',
  orgName = '',
  partySize = 1,
} = {}) {
  // Hard dedupe — Purchase must never refire on refresh/remount/retries.
  // Shares the key with CartContinuePage so the same code never double-fires
  // across confirmation surfaces.
  try {
    const pk = `bf_pix_purch_${confirmationCode}`;
    if (localStorage.getItem(pk)) return;
    localStorage.setItem(pk, '1');
  } catch { /* storage unavailable — fire anyway */ }

  // GA4 standard purchase event — shows in GA4 revenue reports
  _gtag('event', 'purchase', {
    transaction_id: confirmationCode,
    value: totalPrice,
    currency,
    event_category: 'B2C',
    items: [{
      item_id: serviceId,
      item_name: serviceName,
      price: totalPrice,
      quantity: partySize,
      item_brand: orgName,
    }],
  });

  // Google Ads conversion — what B2C campaigns optimise toward
  _gadsConversion(window.BF_CONVERSIONS?.marketplace_booking, totalPrice, currency);

  // Meta Pixel purchase — deterministic eventID for browser/server (CAPI) dedup
  _fbq('Purchase', {
    content_ids: [serviceId],
    content_name: serviceName,
    value: totalPrice,
    currency,
    num_items: partySize,
  }, `purchase_${confirmationCode}`);
}
